import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import * as XLSX from 'xlsx';
import './HOMonthEndView.css';

function HOMonthEndView({ onClose }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [franchisees, setFranchisees] = useState({});
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  // Generate year options (current year and 2 years back)
  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    loadFranchisees();
  }, []);

  useEffect(() => {
    if (Object.keys(franchisees).length > 0) {
      loadSubmissions();
    }
  }, [selectedMonth, selectedYear, franchisees]);

  const loadFranchisees = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'Franchisees'));
      const franchiseesMap = {};
      snapshot.docs.forEach(doc => {
        franchiseesMap[doc.id] = { id: doc.id, ...doc.data() };
      });
      setFranchisees(franchiseesMap);
    } catch (error) {
      console.error('Error loading franchisees:', error);
    }
  };

  const loadSubmissions = async () => {
    setLoading(true);
    try {
      // Query all month-end submissions for the selected month/year
      const q = query(
        collection(db, 'monthEndSubmissions'),
        where('month', '==', selectedMonth),
        where('year', '==', selectedYear)
      );

      const snapshot = await getDocs(q);
      const submissionsList = snapshot.docs.map(doc => {
        const data = doc.data();
        // Get franchisee ID from reference
        let franchiseeId = null;
        if (data.franchiseeRef) {
          if (data.franchiseeRef.id) {
            franchiseeId = data.franchiseeRef.id;
          } else if (data.franchiseeRef.path) {
            franchiseeId = data.franchiseeRef.path.split('/').pop();
          }
        }
        
        return {
          id: doc.id,
          ...data,
          franchiseeId,
          franchiseeName: franchisees[franchiseeId]?.name || 'Unknown Franchisee',
          franchiseeEmail: franchisees[franchiseeId]?.email || ''
        };
      });

      // Sort by franchisee name
      submissionsList.sort((a, b) => a.franchiseeName.localeCompare(b.franchiseeName));
      
      setSubmissions(submissionsList);
    } catch (error) {
      console.error('Error loading submissions:', error);
      alert('Error loading month-end submissions');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissionDetails = async (submission) => {
    if (expandedSubmission === submission.id) {
      setExpandedSubmission(null);
      setDetailData(null);
      return;
    }

    setExpandedSubmission(submission.id);
    setLoadingDetail(true);

    try {
      // Load all students for this franchisee to show the month-end return data
      const franchiseeRef = doc(db, 'Franchisees', submission.franchiseeId);
      
      const studentsQuery = query(
        collection(db, 'students'),
        where('franchiseeRef', '==', franchiseeRef)
      );
      
      const studentsSnapshot = await getDocs(studentsQuery);
      const studentsList = [];

      for (const studentDoc of studentsSnapshot.docs) {
        const studentData = studentDoc.data();
        const termPayments = studentData.termPayments || {};
        
        const termBreakdown = {
          term1: termPayments.term1 || 0,
          term2: termPayments.term2 || 0,
          term3: termPayments.term3 || 0,
          term4: termPayments.term4 || 0,
          admin: termPayments.admin || 0
        };

        const totalTermFees = termBreakdown.term1 + termBreakdown.term2 + 
                              termBreakdown.term3 + termBreakdown.term4;
        
        if (totalTermFees > 0 || termBreakdown.admin > 0) {
          studentsList.push({
            id: studentDoc.id,
            ...studentData,
            termBreakdown,
            totalTermFees,
            totalAdminFee: termBreakdown.admin
          });
        }
      }

      // Sort by school name, then student name
      studentsList.sort((a, b) => {
        const schoolA = a.schoolName || '';
        const schoolB = b.schoolName || '';
        if (schoolA !== schoolB) return schoolA.localeCompare(schoolB);
        return (a.studentName || '').localeCompare(b.studentName || '');
      });

      setDetailData({
        students: studentsList,
        submission: submission
      });
    } catch (error) {
      console.error('Error loading submission details:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Calculate totals for summary
  const calculateTotals = () => {
    let totalRoyalty = 0;
    let totalBB = 0;
    let totalTshirts = 0;
    let totalStickers = 0;
    let totalCourier = 0;
    let totalOther = 0;

    submissions.forEach(sub => {
      totalRoyalty += sub.royaltyAmount || 0;
      totalBB += sub.brandBuildingAmount || 0;
      totalTshirts += sub.tshirtOrders || 0;
      totalStickers += sub.stickerOrdersAmount || 0;
      totalCourier += sub.courierFees || 0;
      totalOther += sub.otherAmount || 0;
    });

    return { totalRoyalty, totalBB, totalTshirts, totalStickers, totalCourier, totalOther };
  };

  const totals = calculateTotals();

  // Get list of franchisees who haven't submitted (excluding test accounts)
  const getNotSubmitted = () => {
    const submittedIds = new Set(submissions.map(s => s.franchiseeId));
    return Object.values(franchisees).filter(f => !submittedIds.has(f.id) && !f.isTest);
  };

  const notSubmitted = getNotSubmitted();

  const downloadExcel = () => {
    const monthLabel = `${monthNames[selectedMonth]} ${selectedYear}`;

    // Build data rows — numbers stay as numbers so Excel can sum them
    const dataRows = submissions.map(sub => {
      const territory = franchisees[sub.franchiseeId]?.territory || '';
      const total = (sub.royaltyAmount || 0) + (sub.tshirtOrders || 0) +
                    (sub.stickerOrdersAmount || 0) + (sub.courierFees || 0) + (sub.otherAmount || 0);
      return {
        'Franchisee':       sub.franchiseeName,
        'Territory':        territory,
        'Royalty (R)':      parseFloat((sub.royaltyAmount || 0).toFixed(2)),
        'T-Shirts (R)':     parseFloat((sub.tshirtOrders  || 0).toFixed(2)),
        'Stickers (R)':     parseFloat((sub.stickerOrdersAmount || 0).toFixed(2)),
        'Courier (R)':      parseFloat((sub.courierFees   || 0).toFixed(2)),
        'Other (R)':        parseFloat((sub.otherAmount   || 0).toFixed(2)),
        'Total to Pay (R)': parseFloat(total.toFixed(2)),
        'Brand Building (R)': parseFloat((sub.brandBuildingAmount || 0).toFixed(2)),
      };
    });

    // Totals row
    const totalsRow = {
      'Franchisee':         'TOTALS',
      'Territory':          '',
      'Royalty (R)':        parseFloat(submissions.reduce((s, r) => s + (r.royaltyAmount || 0), 0).toFixed(2)),
      'T-Shirts (R)':       parseFloat(submissions.reduce((s, r) => s + (r.tshirtOrders  || 0), 0).toFixed(2)),
      'Stickers (R)':       parseFloat(submissions.reduce((s, r) => s + (r.stickerOrdersAmount || 0), 0).toFixed(2)),
      'Courier (R)':        parseFloat(submissions.reduce((s, r) => s + (r.courierFees   || 0), 0).toFixed(2)),
      'Other (R)':          parseFloat(submissions.reduce((s, r) => s + (r.otherAmount   || 0), 0).toFixed(2)),
      'Total to Pay (R)':   parseFloat(submissions.reduce((s, r) => s + (r.royaltyAmount || 0) + (r.tshirtOrders || 0) + (r.stickerOrdersAmount || 0) + (r.courierFees || 0) + (r.otherAmount || 0), 0).toFixed(2)),
      'Brand Building (R)': parseFloat(submissions.reduce((s, r) => s + (r.brandBuildingAmount || 0), 0).toFixed(2)),
    };

    const allRows = [...dataRows, totalsRow];

    // Create worksheet & workbook
    const ws = XLSX.utils.json_to_sheet(allRows);

    // Column widths
    ws['!cols'] = [
      { wch: 24 }, // Franchisee
      { wch: 18 }, // Territory
      { wch: 14 }, // Royalty
      { wch: 14 }, // T-Shirts
      { wch: 12 }, // Courier
      { wch: 12 }, // Other
      { wch: 16 }, // Total to Pay
      { wch: 18 }, // Brand Building
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, monthLabel);

    XLSX.writeFile(wb, `MonthEnd_${monthNames[selectedMonth]}_${selectedYear}.xlsx`);
  };

  return (
    <div className="ho-month-end-container">
      <div className="ho-month-end-header">
        <h1>📊 Month-End Submissions</h1>
        <p className="subtitle">View all franchisee month-end returns</p>
      </div>

      {/* Month/Year Selector */}
      <div className="month-year-selector">
        <div className="selector-group">
          <label>Select Month:</label>
          <select 
            value={selectedMonth} 
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {monthNames.map((month, index) => (
              <option key={index} value={index}>
                {month}
              </option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label>Year:</label>
          <select 
            value={selectedYear} 
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <button onClick={onClose} className="back-btn">← Back</button>
        <button 
          onClick={downloadExcel} 
          className="download-csv-btn"
          disabled={submissions.length === 0}
          title={submissions.length === 0 ? 'No submissions to download' : `Download ${monthNames[selectedMonth]} ${selectedYear} as Excel`}
        >
          📥 Download Excel
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card submissions-card">
          <div className="card-icon">📝</div>
          <div className="card-content">
            <div className="card-label">Submitted</div>
            <div className="card-value">{submissions.length}</div>
          </div>
        </div>

        <div className="summary-card pending-card">
          <div className="card-icon">⏳</div>
          <div className="card-content">
            <div className="card-label">Not Submitted</div>
            <div className="card-value">{notSubmitted.length}</div>
          </div>
        </div>

        <div className="summary-card royalty-card">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <div className="card-label">Total Royalties</div>
            <div className="card-value">R {totals.totalRoyalty.toFixed(2)}</div>
          </div>
        </div>

        <div className="summary-card bb-card">
          <div className="card-icon">🎨</div>
          <div className="card-content">
            <div className="card-label">Brand Building</div>
            <div className="card-value">R {totals.totalBB.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading submissions...</div>
      ) : (
        <>
          {/* Submissions Table */}
          <div className="submissions-section">
            <h2>✅ Submitted ({submissions.length})</h2>
            
            {submissions.length === 0 ? (
              <div className="empty-state">
                <p>No submissions for {monthNames[selectedMonth]} {selectedYear}</p>
              </div>
            ) : (
              <div className="submissions-table-container">
                <table className="submissions-table">
                  <thead>
                    <tr>
                      <th>Franchisee</th>
                      <th>Submitted</th>
                      <th>Royalty</th>
                      <th>T-Shirts</th>
                      <th>Courier</th>
                      <th>Other</th>
                      <th>Brand Building</th>
                      <th>Total to Pay</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map(submission => {
                      const royaltyTotal = (submission.royaltyAmount || 0) + 
                                          (submission.tshirtOrders || 0) + 
                                          (submission.stickerOrdersAmount || 0) +
                                          (submission.courierFees || 0) + 
                                          (submission.otherAmount || 0);
                      
                      return (
                        <React.Fragment key={submission.id}>
                          <tr className={expandedSubmission === submission.id ? 'expanded' : ''}>
                            <td>
                              <div className="franchisee-info">
                                <strong>{submission.franchiseeName}</strong>
                                <small>{submission.franchiseeEmail}</small>
                              </div>
                            </td>
                            <td>
                              {submission.submittedAt?.toDate 
                                ? submission.submittedAt.toDate().toLocaleDateString('en-ZA')
                                : 'N/A'}
                            </td>
                            <td>R {(submission.royaltyAmount || 0).toFixed(2)}</td>
                            <td>R {(submission.tshirtOrders || 0).toFixed(2)}</td>
                            <td>R {(submission.courierFees || 0).toFixed(2)}</td>
                            <td>
                              R {(submission.otherAmount || 0).toFixed(2)}
                              {submission.otherDescription && (
                                <small className="other-desc">({submission.otherDescription})</small>
                              )}
                            </td>
                            <td className="bb-amount">R {(submission.brandBuildingAmount || 0).toFixed(2)}</td>
                            <td className="total-amount">R {royaltyTotal.toFixed(2)}</td>
                            <td>
                              <button 
                                onClick={() => loadSubmissionDetails(submission)}
                                className="view-details-btn"
                              >
                                {expandedSubmission === submission.id ? '▲ Hide' : '▼ View Details'}
                              </button>
                            </td>
                          </tr>

                          {/* Expanded Details */}
                          {expandedSubmission === submission.id && (
                            <tr className="detail-row">
                              <td colSpan="9">
                                {loadingDetail ? (
                                  <div className="loading-detail">Loading details...</div>
                                ) : detailData ? (
                                  <div className="submission-details">
                                    {/* Summary of Deposits */}
                                    <div className="sod-section">
                                      <h3>📋 Summary of Deposits</h3>
                                      <div className="sod-grid">
                                        <div className="sod-item">
                                          <span>Royalty {selectedMonth + 1} {monthNames[selectedMonth]}:</span>
                                          <span>R {(submission.royaltyAmount || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="sod-item">
                                          <span>T-Shirt Orders:</span>
                                          <span>R {(submission.tshirtOrders || 0).toFixed(2)}</span>
                                        </div>
                                        {(submission.stickerOrdersAmount || 0) > 0 && (
                                          <div className="sod-item">
                                            <span>Stickers:</span>
                                            <span>R {(submission.stickerOrdersAmount || 0).toFixed(2)}</span>
                                          </div>
                                        )}
                                        <div className="sod-item">
                                          <span>Courier Fees:</span>
                                          <span>R {(submission.courierFees || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="sod-item">
                                          <span>Other {submission.otherDescription ? `(${submission.otherDescription})` : ''}:</span>
                                          <span>R {(submission.otherAmount || 0).toFixed(2)}</span>
                                        </div>
                                        <div className="sod-item total">
                                          <span>TOTAL ROYALTY ACCOUNT:</span>
                                          <span>R {royaltyTotal.toFixed(2)}</span>
                                        </div>
                                        <div className="sod-item bb-total">
                                          <span>BRAND BUILDING:</span>
                                          <span>R {(submission.brandBuildingAmount || 0).toFixed(2)}</span>
                                        </div>
                                      </div>
                                    </div>

                                    {/* Month-End Return Table */}
                                    <div className="return-section">
                                      <h3>📊 Month-End Return - All Students</h3>
                                      <div className="return-table-container">
                                        <table className="return-table">
                                          <thead>
                                            <tr>
                                              <th>School</th>
                                              <th>Student</th>
                                              <th>Status</th>
                                              <th>Admin</th>
                                              <th>Term 1</th>
                                              <th>Term 2</th>
                                              <th>Term 3</th>
                                              <th>Term 4</th>
                                              <th>Total</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {detailData.students.map(student => {
                                              const tb = student.termBreakdown || {};
                                              const total = (tb.admin || 0) + (tb.term1 || 0) + 
                                                           (tb.term2 || 0) + (tb.term3 || 0) + (tb.term4 || 0);
                                              return (
                                                <tr key={student.id} className={!student.isActive ? 'inactive' : ''}>
                                                  <td>{student.schoolName}</td>
                                                  <td>{student.studentName}</td>
                                                  <td>
                                                    <span className={`status-badge ${student.isActive ? 'active' : 'inactive'}`}>
                                                      {student.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                  </td>
                                                  <td>R {(tb.admin || 0).toFixed(2)}</td>
                                                  <td>R {(tb.term1 || 0).toFixed(2)}</td>
                                                  <td>R {(tb.term2 || 0).toFixed(2)}</td>
                                                  <td>R {(tb.term3 || 0).toFixed(2)}</td>
                                                  <td>R {(tb.term4 || 0).toFixed(2)}</td>
                                                  <td><strong>R {total.toFixed(2)}</strong></td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                          <tfoot>
                                            <tr className="totals-row">
                                              <td colSpan="3"><strong>TOTALS:</strong></td>
                                              <td><strong>R {detailData.students.reduce((sum, s) => sum + (s.termBreakdown?.admin || 0), 0).toFixed(2)}</strong></td>
                                              <td><strong>R {detailData.students.reduce((sum, s) => sum + (s.termBreakdown?.term1 || 0), 0).toFixed(2)}</strong></td>
                                              <td><strong>R {detailData.students.reduce((sum, s) => sum + (s.termBreakdown?.term2 || 0), 0).toFixed(2)}</strong></td>
                                              <td><strong>R {detailData.students.reduce((sum, s) => sum + (s.termBreakdown?.term3 || 0), 0).toFixed(2)}</strong></td>
                                              <td><strong>R {detailData.students.reduce((sum, s) => sum + (s.termBreakdown?.term4 || 0), 0).toFixed(2)}</strong></td>
                                              <td><strong>R {detailData.students.reduce((sum, s) => {
                                                const tb = s.termBreakdown || {};
                                                return sum + (tb.admin || 0) + (tb.term1 || 0) + (tb.term2 || 0) + (tb.term3 || 0) + (tb.term4 || 0);
                                              }, 0).toFixed(2)}</strong></td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                    </div>
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Not Submitted Section */}
          {notSubmitted.length > 0 && (
            <div className="not-submitted-section">
              <h2>⏳ Not Yet Submitted ({notSubmitted.length})</h2>
              <div className="not-submitted-list">
                {notSubmitted.map(franchisee => (
                  <div key={franchisee.id} className="not-submitted-item">
                    <span className="franchisee-name">{franchisee.name}</span>
                    <span className="franchisee-email">{franchisee.email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default HOMonthEndView;